import './index.less'
import * as d3 from 'd3'
import world from '@/assets/data/world.json'
import { useEffect, useState } from 'react'
import { getWorkDistribution } from '@/server'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { setToken } from '@/features/Login/login'
import { useNavigate } from 'react-router-dom'

export function WorkDistribution() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const token = useAppSelector((state) => state.login.token)
  //作品分布博物馆信息
  const [workDistributionMuseum, setWorkDistributionMuseum] = useState<
    { name: string; workNum: number }[]
  >([])
  //作品分布国家信息
  const [workDistributionCountry, setWorkDistributionCountry] = useState([])
  //作品分布地图
  const drawPaintingMap = function (countryInfo) {
    d3.select('#painting-map').selectChildren('*')?.remove()
    // The svg
    const width = document.getElementById('painting-map')?.clientWidth ?? 0,
      height = document.getElementById('painting-map')?.clientHeight ?? 0
    const svg = d3
      .select('#painting-map')
      ?.append('svg')
      .attr('width', width)
      .attr('height', height)
      .call(
        d3
          .zoom()
          .scaleExtent([0.5, 5])
          .on('zoom', function (event) {
            svg.attr('transform', event.transform)
          })
      )
      .append('g')

    // Map and projection
    const projection = d3
      .geoMercator()
      .center([30, 20]) // GPS of location to zoom on
      .scale(110) // This is like the zoom
      .translate([width / 2, height / 2])

    //中英文国家名映射
    const countryNameMap = {
      奥地利: 'Austria',
      澳大利亚: 'Australia',
      德国: 'Germany',
      俄罗斯: 'Russia',
      法国: 'France',
      梵蒂冈: 'Vatican',
      韩国: 'South Korea',
      荷兰: 'Netherlands',
      加拿大: 'Canada',
      捷克: 'Czech Republic',
      美国: 'USA',
      日本: 'Japan',
      瑞典: 'Sweden',
      瑞士: 'Switzerland',
      新加坡: 'Singapore',
      意大利: 'Italy',
      印度: 'India',
      英国: 'England',
      中国: 'China'
    }
    //国家首都对应坐标
    const _posInfo = world.features
      .map((country) => {
        return { name: country.properties.name, pos: country.capital }
      })
      .filter((x) => x.pos)
    const posInfo = {}
    for (let i = 0; i < _posInfo.length; i++) posInfo[_posInfo[i].name] = _posInfo[i].pos
    //地图数据
    const dataGeo = world
    //展示数据
    const data: {
      name: string
      lat: number
      long: number
      num: number
      topValues: { name: string; workNum: number }[]
    }[] = []

    for (let i = 0; i < countryInfo.length; i++) {
      const nameKey = countryInfo[i].name
      data.push({
        name: nameKey,
        long: posInfo[countryNameMap[nameKey]][0],
        lat: posInfo[countryNameMap[nameKey]][1],
        num: countryInfo[i].workNum,
        topValues: countryInfo[i].topValues
      })
    }
    //先渲染半径大的circle
    data.sort((a, b) => b.num - a.num)
    const dataNum = data.map((d) => d.num)
    // Create a color scale
    const colorLinear = d3
      .scaleLinear()
      .domain([d3.min(dataNum), d3.max(dataNum)])
      .range([0, 1])
    const compute = d3.interpolate('#dbd5bc', '#957966')

    // Add a scale for bubble size
    const valueExtent = d3.extent(data, (d) => d.num)
    const size = d3
      .scaleSqrt()
      .domain(valueExtent) // What's in the data
      .range([5, 25]) // Size in pixel

    //地图斑点效果
    const pattern = svg
      .append('defs')
      .append('pattern')
      .attr('id', 'dots')
      .attr('patternUnits', 'userSpaceOnUse')
      .attr('width', 12)
      .attr('height', 12)
    pattern.append('circle').attr('cx', 2).attr('cy', 2).attr('r', 2).attr('fill', '#e4e4e4')
    pattern.append('circle').attr('cx', 8).attr('cy', 2).attr('r', 2).attr('fill', '#e4e4e4')
    pattern.append('circle').attr('cx', 8).attr('cy', 8).attr('r', 2).attr('fill', '#e4e4e4')
    pattern.append('circle').attr('cx', 2).attr('cy', 8).attr('r', 2).attr('fill', '#e4e4e4')

    // Draw the map
    svg
      .append('g')
      .selectAll('path')
      .data(dataGeo.features)
      .join('path')
      .attr('fill', '#b8b8b8')
      .attr('d', d3.geoPath().projection(projection))
      .style('stroke', 'none')
      .attr('fill', 'url(#dots)')
    // create a tooltip
    const Tooltip = d3
      .select('#painting-map')
      .append('div')
      .attr('class', 'tooltip')
      .style('opacity', 1)
      .style('background-color', 'white')
      .style('border', 'solid')
      .style('border-width', '2px')
      .style('border-radius', '5px')
      .style('padding', '5px')
    const mouseover = function (_event, _d) {
      Tooltip.style('opacity', 1)
    }
    const mousemove = function (event, d) {
      const rect = document.getElementById('painting-map')?.getBoundingClientRect()
      Tooltip.html(
        `<div class='tooltip-item'><div class='institute-name'>${
          d.name
        }</div><div class='institute-num'>${d.num}</div></div>${d.topValues
          .map((item, index) => {
            if (item.workNum > 0)
              return (
                "<div class='tooltip-item'> <div class='institute-name'>" +
                (index + 1) +
                '.' +
                item.name +
                "&nbsp;</div><div class='institute-num'>" +
                item.workNum +
                '</div></div>'
              )
          })
          .join('')}`
      )
        .style('left', event.x - (rect?.left ?? 0) + 15 + 'px')
        .style('top', event.y - (rect?.top ?? 0) + 15 + 'px')
    }
    const mouseleave = function (_event, _d) {
      Tooltip.style('opacity', 0)
    }

    // Add circles:
    svg
      .append('g')
      .selectAll('myCircles')
      .data(data)
      .join('circle')
      .attr('cx', (d) => projection([d.long, d.lat])[0])
      .attr('cy', (d) => projection([d.long, d.lat])[1])
      .attr('r', (d) => size(d.num))
      .attr('fill', (d) => compute(colorLinear(d.num)))
      .on('mouseover', mouseover)
      .on('mousemove', mousemove)
      .on('mouseleave', mouseleave)

    // Add texts:
    const fontSize = 10
    svg
      .selectAll('myTexts')
      .data(data)
      .join('text')
      .attr('x', (d) => projection([d.long, d.lat])[0] - (fontSize * d.name.length) / 2)
      .attr('y', (d) => projection([d.long, d.lat])[1] + fontSize / 2)
      .attr('fill', '#ffffff')
      .style('font-size', fontSize)
      .style('font-weight', 400)
      .style('pointer-events', 'none')
      .style('user-select', 'none')
      .text((d) => d.name)
      .attr('display', (d) => {
        return size(d.num) > 10 ? 'block' : 'none'
      })
      .on('mouseover', mouseover)
      .on('mousemove', mousemove)
      .on('mouseleave', mouseleave)
  }
  //获取作品分布信息数据
  useEffect(() => {
    getWorkDistribution(token).then((res) => {
      if (res.data.code === '10056') {
        //token失效
        window.confirm('登录已过期，请重新登录！')
        dispatch(setToken(''))
        navigate('/')
      } else {
        const museumInfo = res.data.content.institute
        const countryInfo = res.data.content.country
        setWorkDistributionMuseum(museumInfo)
        setWorkDistributionCountry(countryInfo)
        drawPaintingMap(countryInfo)
      }
    })
  }, [])
  //浏览器窗口大小改变时，重新绘制svg
  useEffect(() => {
    const func = function () {
      drawPaintingMap(workDistributionCountry)
    }
    window.addEventListener('resize', func)
    return () => {
      window.removeEventListener('resize', func)
    }
  }, [workDistributionCountry])

  return (
    <div className='work-distribution-content'>
      <div className='work-distribution-map'>
        <div id='painting-map'></div>
      </div>
      <div className='work-distribution-list'>
        <div className='work-distribution-list-title'>馆藏件数排名</div>
        <div className='work-distribution-list-content'>
          {workDistributionMuseum?.map((item, index) => {
            if (index > 7) return <></>
            return (
              <div className='work-distribution-list-item' key={index}>
                <div className={'serial-num' + (index < 3 ? ' serial-num-top' : '')}>
                  {index + 1}
                </div>
                <div className='museum-name'>{item.name}</div>
                &nbsp;&nbsp;
                <div className='museum-num'>
                  {String(item.workNum).replace(/(\d)(?=(\d{3})+$)/g, '$1,')}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
